// 📄 pages/Ariba/sections/TablaCatalogToolbox.tsx
// Acceso directo (standalone, sin proyectoId) al Catálogo de Tablas SAP
// desde el Ariba Toolbox — mismo catálogo global de solo lectura que
// /proyecto/:projectId/tablas (ver [[TablasCatalogo.tsx]]), pero accesible
// sin estar dentro de un proyecto puntual, para consulta rápida. Mismo
// patrón exacto que BadiCatalogToolbox.tsx.
import { useEffect, useState } from 'react';
import { RefreshCw, Table2, Info } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import { TablaEstandar, getTablasEstandar } from '../../../services/catalogoTablasService.ts';
import TablaCatalogGrid from '../../../components/tablas/TablaCatalogGrid.tsx';

interface TablaCatalogToolboxProps {
  setPage?: (page: string) => void;
}

export default function TablaCatalogToolbox({ setPage }: TablaCatalogToolboxProps) {
  const [tablas, setTablas] = useState<TablaEstandar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getTablasEstandar()
      .then(setTablas)
      .catch((err) => {
        console.error('Error cargando el catálogo de Tablas:', err);
        setError('No se pudo cargar el catálogo de Tablas.');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="tablas-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Catálogo de Tablas SAP</h1>
          <p className="text-sm text-gray-400 mt-1">Tablas estándar de referencia (ej. vía SE16N) con sus campos, para las integraciones SAP Ariba / S/4HANA.</p>
        </header>

        <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Solución o busca por nombre técnico, transacción o campo.</span>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">Cargando catálogo...</p>
          </div>
        ) : tablas.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <Table2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">Todavía no hay tablas registradas en el catálogo. Un Admin puede cargarlas desde /admin.</p>
          </div>
        ) : (
          <TablaCatalogGrid tablas={tablas} readOnly />
        )}
      </div>
    </main>
  );
}
